import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BellIcon, MessageSquareIcon } from "lucide-react";
import { UserProfileDropdown } from "./UserProfileDropdown";
import { NotificationPanel } from "../notifications/NotificationPanel";
import { useNotifications } from "../../hooks/useNotifications";

interface NavbarProps {
  className?: string;
}

export const Navbar: React.FC<NavbarProps> = ({ className = "" }) => {
  const navigate = useNavigate();
  const [showNotificationPanel, setShowNotificationPanel] = useState(false);
  const { unreadCount, updateUnreadCount } = useNotifications();

  const handleLogoClick = () => {
    navigate("/dashboard");
  };

  const handleNotificationClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowNotificationPanel(!showNotificationPanel);
  };

  const handleCloseNotificationPanel = () => {
    setShowNotificationPanel(false);
  };

  const handleUnreadCountChange = (count: number) => {
    updateUnreadCount(count);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-40 bg-white border-b border-[#f0eff0] ${className}`}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[70px]">
          {/* Logo and Brand - Left Side */}
          <div
            className="flex items-center gap-4 cursor-pointer"
            onClick={handleLogoClick}
          >
            <img
              className="w-10 h-10 sm:w-12 sm:h-12 object-cover"
              alt="AdapTeach Logo"
              src="/logo.png"
            />
            <h1 className="font-medium text-textcolor-1 text-lg sm:text-xl hidden sm:block">
              AdapTeach
            </h1>
          </div>

          {/* Right Side - Always visible elements */}
          <div className="flex items-center gap-3">
            {/* Feedback Button - Desktop only */}
            <a
              href="https://docs.google.com/forms/d/e/1FAIpQLSeLZHlalj3Pu3nUQP_tkO4VFilSp-g5zVxjbqvR8Fd2S3nJOw/viewform?usp=send_form"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:flex items-center gap-2 h-[52px] px-4 rounded-[14.18px] text-white font-medium text-sm transition-all duration-200 hover:opacity-90 hover:shadow-lg"
              style={{
                background:
                  "linear-gradient(93.36deg, #FF8529 0.98%, #FD7053 50%, #FA5A7D 99.02%)",
              }}
              aria-label="Feedback"
            >
              <MessageSquareIcon className="w-[20px] h-[20px]" />
              <span>Feedback</span>
            </a>

            {/* Notification Icon - Desktop only */}
            <div className="relative hidden sm:block">
              <button
                onClick={handleNotificationClick}
                className="relative w-[52px] h-[52px] bg-bgcolor-1 rounded-[14.18px] border-[1.18px] border-[#e3e6eb] flex items-center justify-center hover:bg-gray-100 transition-colors duration-200"
                aria-label="Notifications"
              >
                <BellIcon className="w-[22px] h-[25px] text-textcolor-1" />

                {/* Unread count badge */}
                {unreadCount > 0 && (
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  </div>
                )}
              </button>

              {/* Notification Panel */}
              <NotificationPanel
                isOpen={showNotificationPanel}
                onClose={handleCloseNotificationPanel}
                onUnreadCountChange={handleUnreadCountChange}
              />
            </div>

            {/* User Profile Dropdown - Always visible and contains navigation on mobile */}
            <UserProfileDropdown />
          </div>
        </div>
      </div>
    </nav>
  );
};
